/**
 * Everything the month calendar and its summary line count — all of it pure, so the arithmetic can
 * be argued with in a test rather than through the DOM.
 *
 * The one rule that runs through the whole file: **only days the trainee REPORTED are counted.** A
 * day carries the assigned menu's food whether or not anyone reported it, so an unreported day has
 * totals that look exactly like intake. Averaging them in, or tinting a cell with them, credits a
 * trainee for a plan nobody followed — which is the single way this screen can lie.
 */

import { macro } from "../ui/macroTokens";
import { v2 } from "../ui/v2Tokens";
import { GRAM, KCAL } from "../ui/format";
import { CALORIES, energyFigure, energyLabel, macroFigure, type Figure, type NutritionDisplayMode } from "../ui/nutritionUnits";
import { dayBand, dayDeviationPct, type DayBand } from "../builder/targetsMath";
import type { DaySummaryV2 } from "../../../../services/FoodLogService";
import type { MacroSet } from "../model/nutritionV2Model";

/**
 * The metrics the calendar can be switched to.
 *
 * Fiber is here because it is real: the server resolves it from the catalogue for the serving each
 * row was eaten in (#575), and the day row carries `actual_fiber`. It was left out while the daily
 * tables had no fiber column and every day would have read 0 — a calendar of thirty zeroes is not a
 * view worth offering. That is no longer true, and a fiber column nobody set a goal for simply shows
 * its numbers uncoloured, exactly as any other metric without a target does.
 */
export type LogMetric = "calories" | "protein" | "carbs" | "fats" | "fiber";

export const LOG_METRICS: ReadonlyArray<{
  key: LogMetric;
  label: string;
  unit: string;
  color: string;
  /** calories are whole and grouped ("2,373"); grams keep a decimal — as `remainingLabel` has it.
   *  `label`, `unit` and `whole` are the calories-mode values; display goes through `metricFigure`. */
  whole: boolean;
}> = [
  { key: "calories", label: "קלוריות", unit: KCAL, color: v2.brand, whole: true },
  { key: "protein", label: "חלבון", unit: GRAM, color: macro.protein.text, whole: false },
  { key: "carbs", label: "פחמימות", unit: GRAM, color: macro.carbs.text, whole: false },
  { key: "fats", label: "שומן", unit: GRAM, color: macro.fats.text, whole: false },
  { key: "fiber", label: "סיבים", unit: GRAM, color: macro.fiber.text, whole: false },
];

export const metricOf = (metric: LogMetric) =>
  LOG_METRICS.find((m) => m.key === metric) as (typeof LOG_METRICS)[number];

/**
 * One figure and its unit, formatted the way its metric wants — so the grid and the summary cannot
 * differ. In points mode calories and the three macros become points; fibre has no point value and
 * stays in grams. Only the text changes: bands and deviations are still computed on kcal and grams.
 */
export const metricFigure = (
  value: number,
  metric: LogMetric,
  mode: NutritionDisplayMode = CALORIES
): Figure => {
  if (metric === "calories") return energyFigure(value, mode);
  if (metric === "fiber") return { text: String(Math.round(value * 10) / 10), unit: GRAM };
  return macroFigure(metric, value, mode);
};

export const formatMetric = (value: number, metric: LogMetric, mode: NutritionDisplayMode = CALORIES): string =>
  metricFigure(value, metric, mode).text;

/** The unit beside a metric's figures — `קק״ל` / `גר׳`, or `נק׳` in points mode (fibre aside). */
export const metricUnit = (metric: LogMetric, mode: NutritionDisplayMode = CALORIES): string =>
  metricFigure(0, metric, mode).unit;

/** What the metric is called. Only the energy metric is renamed (`נקודות`); a macro keeps its name. */
export const metricLabel = (metric: LogMetric, mode: NutritionDisplayMode = CALORIES): string =>
  metric === "calories" ? energyLabel(mode) : metricOf(metric).label;

export const metricValue = (macros: MacroSet | undefined, metric: LogMetric): number =>
  macros ? macros[metric] || 0 : 0;

/** A coverage bar segment. `unreported` and `future` are not bands — they are the absence of one. */
export type CoverageSegment = {
  kind: DayBand | "unreported" | "future";
  days: number;
  pct: number;
};

export type MonthSummary = {
  reportedDays: number;
  /** days of the month that have actually HAPPENED — see `daysSoFar` */
  totalDays: number;
  average: number | null;
  deviationPct: number | null;
  /** the band of the AVERAGE — what colours the deviation tag */
  band: DayBand;
  /** three reported days is the floor below which an average says nothing */
  representative: boolean;
  counts: Record<DayBand, number>;
  coverage: CoverageSegment[];
  /** the daily target, when it held steady all month */
  targetPerDay: number | null;
  /** the month had more than one target — so no single figure is honest */
  targetChanged: boolean;
};

const EMPTY_COUNTS: Record<DayBand, number> = { ok: 0, warn: 0, over: 0, under: 0, none: 0 };

/**
 * Percentages that sum to exactly 100.
 *
 * Largest remainder rather than plain rounding: three equal segments must read 34/33/33, and the
 * coverage bar must not end with a hairline of track showing because its parts came to 99.
 */
const shareOut = (counts: number[], total: number): number[] => {
  if (total <= 0) return counts.map(() => 0);
  const exact = counts.map((c) => (c / total) * 100);
  const floors = exact.map(Math.floor);
  let left = 100 - floors.reduce((a, b) => a + b, 0);
  const order = exact
    .map((value, i) => ({ i, remainder: value - Math.floor(value) }))
    .sort((a, b) => b.remainder - a.remainder);
  const out = [...floors];
  for (const { i } of order) {
    if (left <= 0) break;
    out[i] += 1;
    left -= 1;
  }
  return out;
};

/**
 * How many days of the month have happened.
 *
 * For the current month this is days ELAPSED, not `daysInMonth`. On 5 September, "4 מתוך 30" reads
 * as a trainee who has barely logged, when in fact they have logged four days out of five.
 */
export const daysSoFar = (monthStart: string, today: string, daysInMonth: number): number => {
  const month = monthStart.slice(0, 7);
  if (today.slice(0, 7) !== month) return today > month ? daysInMonth : 0;
  return Number(today.slice(8, 10));
};

export const monthSummary = (
  days: DaySummaryV2[],
  metric: LogMetric,
  monthStart: string,
  today: string,
  daysInMonth: number
): MonthSummary => {
  const elapsed = daysSoFar(monthStart, today, daysInMonth);
  const reported = days.filter((day) => day.reported && day.date <= today);

  const counts = { ...EMPTY_COUNTS };
  let sum = 0;
  const targets = new Set<number>();

  for (const day of reported) {
    const value = metricValue(day.totals, metric);
    const target = metricValue(day.targets, metric);
    sum += value;
    counts[dayBand(value, target)] += 1;
    if (target > 0) targets.add(target);
  }

  const average = reported.length > 0 ? sum / reported.length : null;
  const targetPerDay = targets.size === 1 ? [...targets][0] : null;
  // With one target we can say how far the average sat from it. With several we deliberately cannot:
  // a trainee whose goal changed mid-month has no single number to be measured against.
  const deviationPct =
    average !== null && targetPerDay !== null ? dayDeviationPct(average, targetPerDay) : null;

  const future = Math.max(0, daysInMonth - elapsed);
  const unreported = Math.max(0, elapsed - reported.length);
  const kinds: CoverageSegment["kind"][] = ["ok", "warn", "over", "under", "none", "unreported", "future"];
  const raw = kinds.map((kind) =>
    kind === "unreported" ? unreported : kind === "future" ? future : counts[kind as DayBand]
  );
  const pcts = shareOut(raw, daysInMonth);

  return {
    reportedDays: reported.length,
    totalDays: elapsed,
    average,
    deviationPct,
    band: average !== null && targetPerDay !== null ? dayBand(average, targetPerDay) : "none",
    representative: reported.length >= 3,
    counts,
    coverage: kinds
      .map((kind, i) => ({ kind, days: raw[i], pct: pcts[i] }))
      .filter((segment) => segment.days > 0),
    targetPerDay,
    targetChanged: targets.size > 1,
  };
};
